/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@components/common/locale/useLanguage';
import { _ } from '@evershop/evershop/src/lib/locale/translate';

/**
 * ModalNoteInput – Redesigned to match mockup.
 * @param {object} props
 * @param {string}   props.value    - Current note value (Parent state)
 * @param {Function} props.onChange - Called when "SAVE" or "Remove" is clicked
 */
export default function ModalNoteInput(_ref) {
  var {
    value,
    onChange
  } = _ref;
  var {
    isAr
  } = useLanguage();
  var [expanded, setExpanded] = useState(false);
  var [localNote, setLocalNote] = useState(value || '');
  var [isSaved, setIsSaved] = useState(false);
  var containerRef = useRef(null);
  var textareaRef = useRef(null);

  // Sync local note if value changes from outside (e.g. parent reset)
  useEffect(() => {
    setLocalNote(value || '');
    if (!value) {
      setIsSaved(false);
    }
  }, [value]);
  useEffect(() => {
    if (expanded && containerRef.current) {
      var scrollIntoView = () => {
        var _containerRef$current;
        (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 || _containerRef$current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      };
      var id = window.setTimeout(scrollIntoView, 50);
      var id2 = window.setTimeout(scrollIntoView, 350);
      return () => {
        window.clearTimeout(id);
        window.clearTimeout(id2);
      };
    }
    return undefined;
  }, [expanded]);
  var handleToggle = () => {
    setExpanded(prev => !prev);
    // Reset "SAVED" feedback if we expand again
    if (!expanded) setIsSaved(false);
  };
  var handleSave = () => {
    onChange(localNote);
    setIsSaved(true);
    // If we want it to collapse after save, uncomment:
    // setTimeout(() => setExpanded(false), 800);
  };
  var handleRemove = () => {
    onChange('');
    setLocalNote('');
    setIsSaved(false);
    setExpanded(false);
  };

  // State 4: Collapsed with note content
  if (!expanded && value) {
    return /*#__PURE__*/React.createElement("div", {
      ref: containerRef,
      className: "modal-note-container modal-note--has-value"
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-note-header"
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-note-label-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-note-label"
    }, /*#__PURE__*/React.createElement("span", {
      className: "modal-note-icon-v2"
    }, /*#__PURE__*/React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: "19",
      height: "19",
      viewBox: "0 0 19 19",
      fill: "none"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M18.75 9.375C18.75 9.67337 18.6315 9.95952 18.4205 10.1705C18.2095 10.3815 17.9234 10.5 17.625 10.5H10.5V17.625C10.5 17.9234 10.3815 18.2095 10.1705 18.4205C9.95952 18.6315 9.67337 18.75 9.375 18.75C9.07663 18.75 8.79048 18.6315 8.5795 18.4205C8.36853 18.2095 8.25 17.9234 8.25 17.625V10.5H1.125C0.826631 10.5 0.540483 10.3815 0.329505 10.1705C0.118526 9.95952 0 9.67337 0 9.375C0 9.07663 0.118526 8.79048 0.329505 8.5795C0.540483 8.36853 0.826631 8.25 1.125 8.25H8.25V1.125C8.25 0.826631 8.36853 0.540483 8.5795 0.329505C8.79048 0.118526 9.07663 0 9.375 0C9.67337 0 9.95952 0.118526 10.1705 0.329505C10.3815 0.540483 10.5 0.826631 10.5 1.125V8.25H17.625C17.9234 8.25 18.2095 8.36853 18.4205 8.5795C18.6315 8.79048 18.75 9.07663 18.75 9.375Z",
      fill: "#000000"
    }))), /*#__PURE__*/React.createElement("span", {
      className: "modal-note-title"
    }, _('Add Note'))), /*#__PURE__*/React.createElement("p", {
      className: "modal-note-summary"
    }, value)), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "modal-note-remove-btn",
      onClick: handleRemove
    }, _('Remove'))));
  }
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: "modal-note-container ".concat(expanded ? 'modal-note--expanded' : 'modal-note--collapsed')
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-note-header",
    onClick: handleToggle,
    role: "button",
    tabIndex: 0,
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-note-label"
  }, /*#__PURE__*/React.createElement("span", {
    className: "modal-note-icon-v2"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M16.1243 10.1252C16.1243 10.4235 16.0057 10.7097 15.7948 10.9207C15.5838 11.1316 15.2976 11.2502 14.9993 11.2502H8.99926C8.70089 11.2502 8.41474 11.1316 8.20376 10.9207C7.99278 10.7097 7.87426 10.4235 7.87426 10.1252C7.87426 9.82679 7.99278 9.54065 8.20376 9.32967C8.41474 9.11869 8.70089 9.00016 8.99926 9.00016H14.9993C15.2976 9.00016 15.5838 9.11869 15.7948 9.32967C16.0057 9.54065 16.1243 9.82679 16.1243 10.1252ZM14.9993 12.7502H8.99926C8.70089 12.7502 8.41474 12.8687 8.20376 13.0797C7.99278 13.2906 7.87426 13.5768 7.87426 13.8752C7.87426 14.1735 7.99278 14.4597 8.20376 14.6707C8.41474 14.8816 8.70089 15.0002 8.99926 15.0002H14.9993C15.2976 15.0002 15.5838 14.8816 15.7948 14.6707C16.0057 14.4597 16.1243 14.1735 16.1243 13.8752C16.1243 13.5768 16.0057 13.2906 15.7948 13.0797C15.5838 12.8687 15.2976 12.7502 14.9993 12.7502ZM22.1243 12.0002C22.1246 13.7345 21.6795 15.4399 20.8315 16.9528C19.9835 18.4657 18.7611 19.7354 17.2815 20.6402C15.8019 21.545 14.1147 22.0546 12.3816 22.1201C10.6485 22.1856 8.92762 21.8048 7.38394 21.0142L4.34363 22.0314C4.01266 22.143 3.6571 22.1598 3.31705 22.0802C2.977 22.0005 2.66596 21.8274 2.41899 21.5804C2.17202 21.3335 1.99894 21.0224 1.91926 20.6824C1.83958 20.3423 1.85646 19.9868 1.96801 19.6558L2.98144 16.6155C2.28553 15.2523 1.90771 13.7491 1.87638 12.2189C1.84506 10.6887 2.16105 9.17134 2.8006 7.78084C3.44015 6.39035 4.38662 5.16294 5.56883 4.1909C6.75105 3.21886 8.13825 2.52749 9.62614 2.16876C11.114 1.81003 12.6639 1.79328 14.1592 2.11978C15.6545 2.44628 17.0563 3.10752 18.2592 4.05379C19.4622 5.00006 20.4349 6.20673 21.1044 7.58308C21.7738 8.95943 22.1225 10.4696 22.1243 12.0002ZM19.8743 12.0002C19.8738 10.7922 19.5955 9.6006 19.0608 8.51745C18.5261 7.43429 17.7495 6.48865 16.7909 5.75368C15.8323 5.01871 14.7174 4.5141 13.5326 4.2789C12.3478 4.0437 11.1248 4.0842 9.95815 4.39728C8.7915 4.71037 7.7125 5.28763 6.80464 6.08442C5.89678 6.88121 5.18438 7.87617 4.72255 8.99233C4.26072 10.1085 4.06185 11.3159 4.14132 12.5212C4.22078 13.7265 4.57645 14.8974 5.18082 15.9433C5.2607 16.0812 5.31036 16.2345 5.32652 16.3931C5.34269 16.5516 5.32498 16.7118 5.27457 16.863L4.34363 19.6558L7.13644 18.7248C7.25133 18.6866 7.3716 18.667 7.49269 18.6667C7.69028 18.6671 7.88428 18.7195 8.05519 18.8186C9.2524 19.5113 10.611 19.8765 11.9941 19.8774C13.3773 19.8783 14.7364 19.5149 15.9345 18.8237C17.1326 18.1325 18.1275 17.138 18.8191 15.9401C19.5107 14.7422 19.8747 13.3833 19.8743 12.0002Z",
    fill: "#020202"
  }))), /*#__PURE__*/React.createElement("span", {
    className: "modal-note-title"
  }, _('Add Note'))), /*#__PURE__*/React.createElement("div", {
    className: "modal-note-arrow"
  }, expanded ? /*#__PURE__*/React.createElement("svg", {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 15L12 9L6 15",
    stroke: "black",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })) : /*#__PURE__*/React.createElement("svg", {
    width: "19",
    height: "19",
    viewBox: "0 0 19 19",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18.75 9.375C18.75 9.67337 18.6315 9.95952 18.4205 10.1705C18.2095 10.3815 17.9234 10.5 17.625 10.5H10.5V17.625C10.5 17.9234 10.3815 18.2095 10.1705 18.4205C9.95952 18.6315 9.67337 18.75 9.375 18.75C9.07663 18.75 8.79048 18.6315 8.5795 18.4205C8.36853 18.2095 8.25 17.9234 8.25 17.625V10.5H1.125C0.826631 10.5 0.540483 10.3815 0.329505 10.1705C0.118526 9.95952 0 9.67337 0 9.375C0 9.07663 0.118526 8.79048 0.329505 8.5795C0.540483 8.36853 0.826631 8.25 1.125 8.25H8.25V1.125C8.25 0.826631 8.36853 0.540483 8.5795 0.329505C8.79048 0.118526 9.07663 0 9.375 0C9.67337 0 9.95952 0.118526 10.1705 0.329505C10.3815 0.540483 10.5 0.826631 10.5 1.125V8.25H17.625C17.9234 8.25 18.2095 8.36853 18.4205 8.5795C18.6315 8.79048 18.75 9.07663 18.75 9.375Z",
    fill: "#000000"
  })))), expanded && /*#__PURE__*/React.createElement("div", {
    className: "modal-note-body"
  }, /*#__PURE__*/React.createElement("textarea", {
    ref: textareaRef,
    className: "modal-note-textarea",
    placeholder: _('Add Note or Special Request'),
    value: localNote,
    onFocus: () => {
      var _textareaRef$current;
      (_textareaRef$current = textareaRef.current) === null || _textareaRef$current === void 0 || _textareaRef$current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    },
    onChange: e => {
      setLocalNote(e.target.value);
      if (isSaved) setIsSaved(false);
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "modal-note-actions"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "modal-note-save-btn ".concat(isSaved ? 'modal-note-save-btn--saved' : ''),
    disabled: !localNote,
    onClick: handleSave
  }, isSaved ? _('SAVED') : _('SAVE')))));
}